/**
 * Production detail — the FLAT unit-day list.
 *
 * A `shoot_day` is one operating unit on one local date, and that row is the
 * object. The date is an ATTRIBUTE of it, not a parent container. Grouping
 * units under a calendar date would put back the hierarchy migration 060
 * removed, and would make a single-unit production structurally different
 * from a two-unit one when they are the same shape.
 *
 * Repeated dates are therefore normal and expected. Consecutive rows sharing
 * a date get a lighter divider so the eye can scan — typography only, no
 * nesting.
 *
 * TIMING HERE IS PLANNED, and says so. Actual DPR timing is a different fact
 * and is not built; the row is laid out in labelled slots so it can gain an
 * ACTUAL column without redesigning the screen.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  modeller, unitLabel, type Estimate, type Production, type ShootDay,
} from '../api';
import { canWrite } from '../../api/client';
import { PageHeader } from '../shell/Shell';
import { Empty, ErrorState, Loading } from '../shell/states';
import { Note, Tag } from '../shell/provenance';
import './detail.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortDate(iso: string): string {
  const p = iso.split('-');
  return `${Number(p[2])} ${MONTHS[Number(p[1]) - 1] ?? p[1]}`;
}

/** `08:30:00` -> `08:30`. Never reformatted beyond dropping seconds. */
function hhmm(t: string | null): string | null {
  return t ? t.slice(0, 5) : null;
}

/**
 * Deterministic ordering: local date, then unit. The API orders by date; unit
 * order within a date is settled here so two units on one date never swap
 * between renders.
 */
function ordered(days: ShootDay[]): ShootDay[] {
  return [...days].sort((a, b) =>
    a.local_date.localeCompare(b.local_date) ||
    (a.production_unit_id ?? 0) - (b.production_unit_id ?? 0) ||
    a.id - b.id);
}

function UnitDayRow({ day, repeatsDate }: { day: ShootDay; repeatsDate: boolean }) {
  const call = hhmm(day.call_time_local);
  const wrap = hhmm(day.estimated_wrap_local);
  const preRig = hhmm(day.pre_rig_local);

  return (
    <li className={repeatsDate ? 'pem-day pem-day--same-date' : 'pem-day'}>
      {/* Date and unit are the SAME visual level. Unit identity is never
          metadata: on 15 April the 1st Unit was called at 12:00 and the 2nd
          at 06:00, and which is which is the whole point of the row. */}
      <div className="pem-day__date">
        <span className={repeatsDate ? 'pem-day__date-repeat' : undefined}>
          {shortDate(day.local_date)}
        </span>
      </div>
      <div className="pem-day__unit">{unitLabel(day)}</div>

      <div className="pem-day__timing">
        <span className="pem-label pem-day__basis">Planned</span>
        <div className="pem-day__times">
          {call
            ? <span className="pem-day__time"><em>Call</em> {call}</span>
            : <span className="pem-day__time pem-day__time--none">No crew call</span>}
          {/* No manufactured range. A day with no wrap reads "Call 12:00",
              not "12:00 → —", which draws an arrow to nothing. */}
          {wrap && <span className="pem-day__time"><em>Wrap</em> {wrap}</span>}
          {preRig && <span className="pem-day__time pem-day__time--minor"><em>Pre-rig</em> {preRig}</span>}
        </div>
        {day.planned_length_h != null && (
          <div className="pem-day__duration">
            {day.planned_length_h} h
            {day.day_length_is_derived && (
              /* Derived is a PROVENANCE statement, not a warning: the length
                 is call-to-wrap arithmetic rather than an authored number. */
              <> · <span className="pem-day__derived">derived</span></>
            )}
          </div>
        )}
      </div>

      <div className="pem-day__meta">
        {day.day_number != null && <Tag tone="muted">Day {day.day_number}</Tag>}
        {day.lighting_condition && <Tag tone="assumed">{day.lighting_condition}</Tag>}
        {day.supply_context && <Tag tone="assumed">{day.supply_context}</Tag>}
      </div>
    </li>
  );
}

/** The one write this screen offers, and only to a session that holds an
 *  editing key. A control that exists and 401s makes the product look broken
 *  rather than unfinished. */
function AddDayButton({ slug }: { slug: string }) {
  if (!canWrite()) return null;
  return (
    <Link className="pem-btn" to={`/modeller/productions/${slug}/days/new`}>
      Add shooting day
    </Link>
  );
}

type State =
  | { t: 'loading' } | { t: 'error'; e: unknown } | { t: 'ok'; p: Production };

export function ProductionDetail() {
  const { slug = '' } = useParams();
  const [state, setState] = useState<State>({ t: 'loading' });
  const [estimates, setEstimates] = useState<Estimate[]>([]);

  useEffect(() => {
    let live = true;
    /* Reset on slug change: without this, navigating between productions
       renders the previous one's days under the new one's title until the
       request lands. */
    setState({ t: 'loading' });
    setEstimates([]);
    modeller.production(slug)
      .then((p) => { if (live) setState({ t: 'ok', p }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    modeller.estimates(slug)
      .then((e) => { if (live) setEstimates(e); })
      /* An estimate list that fails must not take the schedule down with it. */
      .catch(() => { if (live) setEstimates([]); });
    return () => { live = false; };
  }, [slug]);

  if (state.t === 'loading') return <><PageHeader title="Production" /><Loading what={slug} /></>;
  if (state.t === 'error') return <><PageHeader title="Production" /><ErrorState what={slug} error={state.e} /></>;

  const { p } = state;
  const days = ordered(p.shoot_days);

  return (
    <>
      <PageHeader
        title={p.name}
        count={`${days.length} unit-day${days.length === 1 ? '' : 's'}`}
        actions={
          <>
            <AddDayButton slug={p.slug} />
            <Link className="pem-link" to="/modeller/productions">← Productions</Link>
          </>
        }
      />

      <div className="pem-subhead">
        {p.production_company && <span>{p.production_company}</span>}
        <span>{p.timezone}</span>
        <span>{p.units.length} unit{p.units.length === 1 ? '' : 's'}</span>
      </div>

      {days.length === 0 ? (
        <Empty
          title="No shooting days yet"
          body={
            'Each row here is one unit on one date — a 1st Unit day and a 2nd ' +
            'Unit day on the same date are two separate rows with their own ' +
            'call times. ' +
            (canWrite()
              ? 'Add the first one to start the schedule.'
              : 'Adding one needs an editing key, issued by Equoia.')
          }
          action={<AddDayButton slug={p.slug} />}
        />
      ) : (
        <ol className="pem-days">
          {days.map((d, i) => (
            <UnitDayRow
              key={d.id}
              day={d}
              repeatsDate={i > 0 && days[i - 1]!.local_date === d.local_date}
            />
          ))}
        </ol>
      )}

      <div className="pem-detail-links">
        {/* The NEWEST estimate, looked up — never a hardcoded id. A fixture id
            baked in here linked live users to an estimate that does not
            exist. */}
        {estimates.length > 0 ? (
          <Link className="pem-link"
                to={`/modeller/productions/${p.slug}/estimates/${estimates[0]!.id}`}>
            Latest estimate →
          </Link>
        ) : (
          <span className="pem-provenance">No estimates yet</span>
        )}
        <Link className="pem-link" to={`/modeller/productions/${p.slug}/documents`}>
          Documents →
        </Link>
      </div>

      <div className="pem-detail-foot">
        <Note>
          Times shown are planned. Actual timing from the daily production
          report is a separate fact and is not recorded here yet.
        </Note>
      </div>
    </>
  );
}
